import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  FaShieldAlt, 
  FaVideo, 
  FaTools, 
  FaUserCheck, 
  FaCheckCircle, 
  FaArrowRight, 
  FaPhoneAlt,
  FaServer,
  FaHdd,
  FaPlug
} from "react-icons/fa";
import "./SecuritySystem.css";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.1, ease: [0.25, 0.1, 0.25, 1] }
  })
};

const SecuritySystem = () => {
  const navigate = useNavigate();

  const cameraTypes = [
    { 
      name: "Dome & Bullet Cameras", 
      imgSrc: "/images/cctv/3.jpg",
      specs: ["4K Ultra-HD Resolution", "30m Infrared Night Vision", "IP67 Weatherproof Shell", "Power over Ethernet (PoE)"]
    },
    { 
      name: "PTZ (Pan-Tilt-Zoom) Cameras", 
      imgSrc: "/images/cctv/1.jpg",
      specs: ["360° Continuous Pan", "25x Optical Autofocus Zoom", "Auto AI Human Tracking", "High-Speed Pan Motor"]
    },
    { 
      name: "Network/IP Cameras", 
      imgSrc: "/images/cctv/2.jpg",
      specs: ["Onvif Profile S/G Sync", "Built-in Edge Storage Slot", "WDR 120dB Light Contrast", "H.265+ Codec Compression"]
    },
    { 
      name: "Wireless & IoT Cameras", 
      imgSrc: "/images/cctv/4.jpg",
      specs: ["Dual-Band 2.4/5GHz Wi-Fi", "Two-Way Clear Audio Speak", "Mobile App Push Alerts", "Cloud Record Integration"]
    },
    { 
      name: "Analog / HD-TVI Cameras", 
      imgSrc: "/images/cctv/61cV3MWS5OL.jpg",
      specs: ["Analog BNC Cable Port", "Up to 5MP Crisp Clarity", "Zero Signal Latency Live", "Budget-Friendly Scalability"]
    },
    { 
      name: "Starlight / Low-Light Cameras", 
      imgSrc: "/images/cctv/hd-cctv-camera.jpg",
      specs: ["F1.0 Full-Color Aperture", "24/7 Vivid Color Capture", "Ultra-Low Light Sensor", "Warm LED Supplementary Light"]
    },
    { 
      name: "4G Mobile SIM Cameras", 
      imgSrc: "/images/cctv/IMG_2561-no-logo-scaled-1-800x533.webp",
      specs: ["Integrated 4G SIM Modem", "100% Wire-Free Remote Sites", "Solar Panel Compatible", "Perfect for Farm & Construction"]
    },
    { 
      name: "High-Performance NVR/DVR System", 
      imgSrc: "/images/cctv/1-4g-sim-linkage-camera-indoor-outdoor-security-camera-trueview-original-imah3yg3ggvewfcr.webp",
      specs: ["Multi-Channel Live Splitting", "Continuous 24/7 Playback", "Intelligent Smart Search", "HDMI 4K Monitor Outputs"]
    },
  ];

  return (
    <div className="security-page-wrapper">
      {/* ── 1. CINEMATIC HERO SECTION ── */}
      <header className="security-header" style={{ backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0.65)), url("/cctv/cctv-back-cover.png")' }}>
        <div className="security-header-content">
          <div className="header-tagline-badge">
            <span className="live-dot" /> Enterprise Surveillance Solutions
          </div>
          <h1>Advanced Premium <br /><span className="gradient-text-hero">CCTV Solutions</span></h1>
          <p>
            At iMatrix Technologies, we design and deploy comprehensive high-definition surveillance 
            architectures for commercial offices, industrial warehouses, and smart homes across Tamil Nadu.
          </p>

          <div className="hero-ctas">
            <button className="btn-primary" onClick={() => navigate('/contact')}>
              Book Free Site Survey <FaArrowRight />
            </button>
            <button className="btn-secondary-hero" onClick={() => navigate('/contact')}>
              <FaPhoneAlt /> Request a Quote
            </button>
          </div>
        </div>
      </header>

      <div className="security-page-container security-root">
        
        {/* ── 2. BRAND TRUST TICKER BAR ── */}
        <section className="brand-trust-ticker-section">
          <div className="ticker-label">CERTIFIED INTEGRATION PARTNERS</div>
          <div className="ticker-grid">
            <div className="brand-logo-card">HIKVISION</div>
            <div className="brand-logo-card">HONEYWELL</div>
            <div className="brand-logo-card">CP PLUS</div>
            <div className="brand-logo-card">DAHUA</div>
          </div>
        </section>

        {/* ── 3. CORE SERVICES SHOWCASE (GLASS CARDS) ── */}
        <section className="security-services-section">
          <div className="showcase-header">
            <span className="tagline">Surveillance Engineering</span>
            <h2>Our Core Security Services</h2>
            <p>Professional camera placements, high-speed networking, and enterprise configurations.</p>
          </div>

          <div className="security-services-grid">
            <div className="security-service-card">
              <div className="security-card-image">
                <img src="/images/cctv/blurred-shop-store-background-captured-by-cctv-security-panorama_908985-57088.avif" alt="Top Tier Brands" />
              </div>
              <div className="security-card-content">
                <div className="card-icon-box"><FaShieldAlt /></div>
                <h3>Leading Camera Brands</h3>
                <p>
                  We supply and service premium devices from Hikvision, CP Plus, and Honeywell, offering 
                  highly reliable bullet, dome, and PTZ hardware configured to deliver consistent performance.
                </p>
              </div>
            </div>

            <div className="security-service-card">
              <div className="security-card-image">
                <img src="/images/cctv/pngtree-cctv-camera-in-3d-rendering-image_3674737.jpg" alt="Surveillance Installation" />
              </div>
              <div className="security-card-content">
                <div className="card-icon-box"><FaTools /></div>
                <h3>Expert System Installation</h3>
                <p>
                  Certified technician crews deploy clean coaxial or fiber runs, configure switches, 
                  and mount lenses at optimal coordinates for maximum area coverage.
                </p>
              </div>
            </div>

            <div className="security-service-card">
              <div className="security-card-image">
                <img src="/images/cctv/young-couple-using-touchpad-with-insurance-agent-while-making-loan-repayment-plan-meeting.jpg" alt="CCTV Consultation" />
              </div>
              <div className="security-card-content">
                <div className="card-icon-box"><FaUserCheck /></div>
                <h3>Custom Site Consultation</h3>
                <p>
                  From single-camera residential setups to multi-site optical networks, we audit your 
                  property and design custom surveillance matrices tailored to your budget constraints.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── 4. ENTERPRISE CAPABILITY HIGHLIGHTS ── */}
        <section className="security-feature-row">
          <div className="security-feature-item">
            <div className="feat-icon"><FaServer /></div>
            <h3>DVR & NVR Video Servers</h3>
            <p>
              High-performance Digital and Network Video Recorder hubs offering secure local storage, 
              multichannel views, and seamless iOS/Android remote access app integration.
            </p>
          </div>
          <div className="security-feature-item">
            <div className="feat-icon"><FaHdd /></div>
            <h3>Continuous Surveillance HDDs</h3>
            <p>
              Enterprise-grade Western Digital Purple & Seagate SkyHawk hard drives optimized for 
              uninterrupted, 24/7 continuous high-definition write cycles.
            </p>
          </div>
          <div className="security-feature-item">
            <div className="feat-icon"><FaPlug /></div>
            <h3>Premium Mounts & Access Components</h3>
            <p>
              Heavy-duty junction boxes, surge protectors, precision mounts, and certified copper 
              cabling to ensure 100% operational uptime during grid events.
            </p>
          </div>
        </section>

        {/* ── 5. INTERACTIVE CAMERA OPTION SPEC CATALOG ── */}
        <section className="camera-catalog-section">
          <div className="showcase-header">
            <span className="tagline">Surveillance Catalog</span>
            <h2>Explore Camera Specifications</h2>
            <p>Compare technical capabilities and integration options across our active hardware configurations.</p>
          </div>

          <div className="camera-types-grid">
            {cameraTypes.map((camera, index) => (
              <div key={index} className="camera-type-card">
                <div className="camera-img-box">
                  <img src={camera.imgSrc} alt={camera.name} />
                </div>
                <div className="camera-card-info">
                  <h3>{camera.name}</h3>
                  <ul className="camera-spec-list">
                    {camera.specs.map(spec => (
                      <li key={spec}><FaCheckCircle className="spec-bullet" /> {spec}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </section>

      </div>
    </div>
  );
};

export default SecuritySystem;
